// Analytics consent state (SEC-6).
//
// The member app loads Google Analytics (GA4/gtag) and Firebase Analytics, both
// of which egress usage data — including the Firebase UID and Google Click ID —
// to Google endpoints in the United States. For a UK health service that is an
// international transfer and a consent exposure, so no analytics may load until
// the visitor has explicitly granted consent.
//
// This module is the single source of truth for that decision. It is framework-
// agnostic (no React) and safe to import in any environment (SSR/jsdom guarded).

export type AnalyticsConsentStatus = 'granted' | 'denied';

const STORAGE_KEY = 'melio_analytics_consent';

// Bump this whenever the analytics / cookie policy materially changes so that
// previously-recorded choices are treated as stale and visitors are re-prompted.
export const ANALYTICS_CONSENT_VERSION = '2026-07-analytics-v1';

interface StoredConsent {
  status: AnalyticsConsentStatus;
  version: string;
}

type Listener = (status: AnalyticsConsentStatus) => void;
const listeners = new Set<Listener>();

function readStored(): StoredConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    // Ignore choices made against a superseded policy version, or malformed data.
    if (!parsed || parsed.version !== ANALYTICS_CONSENT_VERSION) return null;
    if (parsed.status !== 'granted' && parsed.status !== 'denied') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Current choice, or `null` if the visitor has not decided yet. */
export function getAnalyticsConsent(): AnalyticsConsentStatus | null {
  return readStored()?.status ?? null;
}

/** True only when analytics is allowed to load. */
export function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsent() === 'granted';
}

/** Persist the visitor's choice and notify subscribers. */
export function setAnalyticsConsent(status: AnalyticsConsentStatus): void {
  if (typeof window !== 'undefined') {
    try {
      const record: StoredConsent = { status, version: ANALYTICS_CONSENT_VERSION };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Storage can throw in private mode / when disabled — still notify so the
      // in-memory listeners react for the current session.
    }
  }
  listeners.forEach((listener) => listener(status));
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function subscribeAnalyticsConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Run `callback` once, as soon as consent is (or becomes) granted. If consent is
 * already granted it runs synchronously. If the visitor never grants consent the
 * callback never runs. Returns an unsubscribe function to cancel a pending wait.
 */
export function whenAnalyticsConsentGranted(callback: () => void): () => void {
  if (hasAnalyticsConsent()) {
    callback();
    return () => {};
  }
  const unsubscribe = subscribeAnalyticsConsent((status) => {
    if (status === 'granted') {
      unsubscribe();
      callback();
    }
  });
  return unsubscribe;
}
